import { useCallback, useEffect, useRef, useState } from "react";
import { clampToRanges, EMPTY_TIMELINE, readTimeline } from "../playback/timeline";

export const FRAME_SEC = 1 / 30;

export const usePlayback = (playerRef, sourceKind = "file") => {
  const [playing, setPlaying] = useState(true);
  const [played, setPlayed] = useState(0);
  const [timeline, setTimeline] = useState(EMPTY_TIMELINE);
  const [sourceVersion, setSourceVersion] = useState(0);
  const pendingFrame = useRef(null);
  const pendingTimer = useRef(null);
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const clearPending = useCallback(() => {
    pendingFrame.current = null;
    clearTimeout(pendingTimer.current);
  }, []);
  useEffect(() => clearPending, [clearPending]);
  const syncTimeline = useCallback(() => {
    const next = readTimeline(playerRef.current, sourceKind);
    setTimeline((old) => JSON.stringify(old) === JSON.stringify(next) ? old : next);
    return next;
  }, [playerRef, sourceKind]);
  useEffect(() => { clearPending(); syncTimeline(); }, [clearPending, syncTimeline]);
  const playbackChanged = useCallback((value) => {
    if (value) clearPending();
    playingRef.current = value;
    setPlaying(value);
  }, [clearPending]);
  const togglePlay = () => playbackChanged(!playingRef.current);
  const seek = (seconds, { frame = false, direction = 0, resume = playingRef.current } = {}) => {
    const player = playerRef.current;
    if (!player || !Number.isFinite(seconds)) return;
    if (!frame) clearPending();
    const current = syncTimeline();
    if (current.kind !== "youtube-live" && !current.ranges.length) { clearPending(); return; }
    const target = clampToRanges(seconds, current.ranges, direction);
    if (frame) {
      const media = player.getInternalPlayer?.();
      media?.pause?.();
      media?.pauseVideo?.();
      playingRef.current = false;
      setPlaying(false);
      pendingFrame.current = target;
      clearTimeout(pendingTimer.current);
      pendingTimer.current = setTimeout(() => {
        clearPending();
        const actual = player.getCurrentTime?.();
        if (playerRef.current === player && Number.isFinite(actual)) setPlayed(Math.max(0, actual));
      }, 5000);
    }
    try {
      player.seekTo(target, "seconds", frame ? false : resume);
      setPlayed(target);
    } catch {
      clearPending();
      syncTimeline();
    }
  };
  const seekToSeconds = (seconds) => seek(seconds);
  const seekBy = (delta) => seek(playerRef.current?.getCurrentTime?.() + delta, { direction: Math.sign(delta) });
  const stepFrame = (direction) => {
    const from = pendingFrame.current ?? playerRef.current?.getCurrentTime?.();
    seek(from + direction * FRAME_SEC, { frame: true, direction: Math.sign(direction) });
  };
  const seeked = useCallback((seconds) => {
    const player = playerRef.current;
    const media = player?.getInternalPlayer?.();
    const actual = player?.getCurrentTime?.();
    // A native seeked event is final even if the decoder corrected the target.
    // Ignore queued older events while a newer seek is active, or if their
    // reported position is no longer the current media position.
    if (pendingFrame.current != null && media?.seeking !== true && Number.isFinite(actual)
      && (!Number.isFinite(seconds) || Math.abs(actual - seconds) < FRAME_SEC / 2)) {
      clearPending();
      setPlayed(Math.max(0, actual));
    }
  }, [clearPending, playerRef]);
  const onProgress = (progress) => {
    const seconds = progress.playedSeconds;
    if (!Number.isFinite(seconds)) return;
    // YouTube has no seeked event. Progress confirms arrival at our latest
    // target; earlier samples must not clear a rapidly accumulated request.
    if (pendingFrame.current != null && Math.abs(seconds - pendingFrame.current) < FRAME_SEC / 2) clearPending();
    if (pendingFrame.current == null) setPlayed(Math.max(0, seconds));
    syncTimeline();
  };
  const reset = useCallback(() => {
    clearPending();
    setPlayed(0);
    setTimeline(EMPTY_TIMELINE);
    setSourceVersion((v) => v + 1);
  }, [clearPending]);
  const failed = useCallback(() => {
    clearPending();
    setTimeline(EMPTY_TIMELINE);
    setPlaying(false);
    playingRef.current = false;
    setSourceVersion((v) => v + 1);
  }, [clearPending]);
  const goLive = () => {
    const current = syncTimeline();
    if (current.liveTarget == null) return;
    seek(current.liveTarget, { resume: true });
    playbackChanged(true);
  };
  return { playing, played, timeline, sourceVersion, togglePlay, playbackChanged, seekBy, stepFrame, seekToSeconds, seeked, onProgress, syncTimeline, reset, failed, goLive };
};
